import { useState, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { some, isEqual, isBoolean } from 'lodash-es';

import { getDropdownRootContainer } from '../../../utils/helpers';

const isPropsChanged = (custom, base) => some(custom, (value, key) => !isEqual(value, base[key]));

export function useMenuProps(customProps) {
  const rootMenuContainerRef = useRef();

  // List of all props: https://szhsin.github.io/react-menu/docs#menu
  const [props, setProps] = useState({
    boundingBoxPadding: '40 24',
    gap: 8,
    overflow: 'auto',
    position: 'anchor',
    portal: true,
  });

  useEffect(() => {
    setProps((prevState) => {
      if (isPropsChanged(customProps, prevState)) return { ...prevState, ...customProps };
      return prevState;
    });
  }, [customProps]);

  const menuProps = useMemo(() => {
    const setUpMenuPortal = () => {
      if (!props.portal) return {};

      if (isBoolean(props.portal) && props.portal) {
        // Set root menu container once
        if (!rootMenuContainerRef.current) {
          rootMenuContainerRef.current = { target: getDropdownRootContainer() };
        }

        if (rootMenuContainerRef.current.target) {
          return { portal: rootMenuContainerRef.current };
        }
      }

      return { portal: props.portal };
    };

    return {
      ...props,
      ...setUpMenuPortal(),
      menuClassName: cx('hms-dropdown-menu', props.menuClassName),
      className: cx('hms-dropdown-menu-container', props.className),
    };
  }, [props]);

  return menuProps;
}

export function useSubMenuProps(customProps) {
  // List of all props: https://szhsin.github.io/react-menu/docs#submenu
  const [props, setProps] = useState({
    gap: 20,
    overflow: 'auto',
    position: 'anchor',
  });

  useEffect(() => {
    setProps((prevState) => {
      if (isPropsChanged(customProps, prevState)) return { ...prevState, ...customProps };
      return prevState;
    });
  }, [customProps]);

  const menuProps = useMemo(
    () => ({
      ...props,
      menuClassName: cx('hms-dropdown-menu', props.menuClassName),
    }),
    [props]
  );

  return menuProps;
}
